import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  IPaginatedService,
  IPaginatedServiceParamters,
} from 'src/app/core/models/ITable';
import {
  AccountMovement,
  MovementTypeEnum,
  MovementTypeUrl,
} from 'src/app/core/models/AccountMovement';
import { TranslateService } from '../../services/translate.service';
import { DateService } from '../../services/date.service';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { ContractBalanceAccountService } from '../../services/contract-balance-account.service';
import { ToastService } from '../../services/toast.service';

@Component({
  selector: 'app-balance-account-grid',
  templateUrl: './balance-account-grid.component.html',
  styleUrls: ['./balance-account-grid.component.css'],
})
export class BalanceAccountGridComponent implements OnInit {
  @Input() tableService: IPaginatedService;
  @Input() id: number;
  @Input() itemsPerPage: number = 10;
  @Input() contractMode: boolean = false;
  @Input() contractCurrency: number = null;

  iconQuestion = faQuestionCircle;

  accountMovements: AccountMovement[];
  page: number = 1;

  totalItems: number = 10;
  periodVal: number = 1;
  currencyVal: number = 1;
  query: string = 'CreatedOrNot=true';
  emptyTable: boolean = true;

  // Propiedades para exportación
  selectedExportType: 'csv' | 'excel' | null = null;
  selectedScope: 'current' | 'all' | null = null;
  showExportDropdown: boolean = false;
  isExporting: boolean = false;

  constructor(
    public translate: TranslateService,
    private router: Router,
    public dateService: DateService,
    private contractBalanceAccountService: ContractBalanceAccountService,
    private toastService: ToastService,
  ) {}

  ngOnInit() {
    this.currencyVal = this.contractCurrency ? this.contractCurrency : 1;
    this.getElements(this.page, this.itemsPerPage);
  }

  getElements(page, itemsPerPage) {
    var paginated: IPaginatedServiceParamters = {
      id: this.id,
      query: this.query,
      pageNumber: page,
      pageSize: itemsPerPage,
      period: this.periodVal,
      currency: this.currencyVal,
    };

    this.tableService.getAllPaginated(paginated).subscribe(
      (response: any) => {
        this.accountMovements = response.result;
        this.emptyTable = false;
        this.totalItems = response.pagination.totalItems;
      },
      (err) => {
        this.emptyTable = true;
        this.accountMovements = [];
      },
    );
  }

  pageChanged(event) {
    this.page = event;
    this.getElements(event, this.itemsPerPage);
  }

  setPeriod(val) {
    this.periodVal = val;
    this.getElements(this.page, this.itemsPerPage);
  }

  setCurrency(val) {
    this.currencyVal = val;
    this.getElements(this.page, this.itemsPerPage);
  }

  movementTypes(value): string {
    return MovementTypeEnum[value];
  }

  movementTypesUrl(value): string {
    return MovementTypeUrl[value];
  }

  redirect(id, type) {
    if (
      type != MovementTypeEnum.PendingReturn ||
      MovementTypeEnum.PendingFee ||
      MovementTypeEnum.PaymentDeletion ||
      MovementTypeEnum.ReturnDeletion ||
      MovementTypeEnum.FeeDeletion
    ) {
      this.router.navigateByUrl(this.movementTypesUrl(type) + id);
    }
  }

  // Métodos para exportación
  openExportDropdown(type: 'csv' | 'excel') {
    this.selectedExportType = type;
    this.showExportDropdown = !this.showExportDropdown;
  }

  onScopeSelect(scope: 'current' | 'all') {
    this.selectedScope = scope;
    this.showExportDropdown = false;
    
    if (this.selectedExportType && this.selectedScope) {
      this.executeExport();
    }
  }

  private executeExport() {
    if (!this.selectedExportType || !this.selectedScope) {
      return;
    }

    this.isExporting = true;
    this.toastService.showToast('info', 'Preparando exportación...');

    const params = this.getExportParams();

    if (this.selectedExportType === 'csv') {
      this.exportToCSV(params);
    } else if (this.selectedExportType === 'excel') {
      this.exportToExcel(params);
    }
  }

  private getExportParams() {
    const baseParams = {
      id: this.id,
      query: this.query,
      period: this.periodVal,
      currency: this.currencyVal,
    };

    if (this.selectedScope === 'current') {
      // Solo la página actual
      return {
        ...baseParams,
        pageNumber: this.page,
        pageSize: this.itemsPerPage,
      };
    } else {
      // Todos los registros que cumplen los filtros
      return {
        ...baseParams,
        pageNumber: 1,
        pageSize: 100000, // Número grande para obtener todos
      };
    }
  }

  private exportToCSV(params: any) {
    this.contractBalanceAccountService.downloadCSV(params).subscribe(
      (blob: Blob) => {
        this.downloadFile(blob, 'csv', 'CuentaCorriente');
        this.isExporting = false;
        this.toastService.showToast('success', 'Archivo CSV descargado exitosamente');
      },
      (error) => {
        this.isExporting = false;
        this.toastService.showToast('error', 'Error al descargar el archivo CSV');
        console.error('Error al descargar CSV:', error);
      }
    );
  }

  private exportToExcel(params: any) {
    this.contractBalanceAccountService.downloadExcel(params).subscribe(
      (blob: Blob) => {
        this.downloadFile(blob, 'excel', 'CuentaCorriente');
        this.isExporting = false;
        this.toastService.showToast('success', 'Archivo Excel descargado exitosamente');
      },
      (error) => {
        this.isExporting = false;
        this.toastService.showToast('error', 'Error al descargar el archivo Excel');
        console.error('Error al descargar Excel:', error);
      }
    );
  }

  private downloadFile(blob: Blob, type: 'csv' | 'excel', filename: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    
    const today = new Date();
    const day = today.getDate().toString().padStart(2, '0');
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const year = today.getFullYear();
    const formattedDate = `${day}-${month}-${year}`;
    
    const scopeText = this.selectedScope === 'current' ? 'Vista' : 'Completo';
    const extension = type === 'csv' ? 'csv' : 'xlsx';
    
    a.download = `${filename}_${scopeText}_${formattedDate}.${extension}`;
    a.click();
    window.URL.revokeObjectURL(url);
  }
}
